import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { preguntasDe } from '#components/mobile/encuesta/preguntas';
import type {
  NombreEncuesta,
  Pregunta,
} from '#components/mobile/encuesta/preguntas';
import { Boton } from '#components/mobile/ui/Boton';
import { color, texto } from '#components/mobile/ui/tokens';
import { useLanguage } from '#hooks/useLocale';

import { ListaRecuentos, Seccion } from './piezas';
import { fechaCorta } from './puente';
import type { ResumenEncuesta, ResumenPuente } from './puente';

/**
 * Pestaña «Encuesta» del Puente de mando: cuántas personas eligieron cada
 * opción, lo que escriben y quién acepta una charla de 10 minutos.
 */
export function EncuestaPuente({
  datos,
  onPersona,
}: {
  datos: ResumenPuente;
  onPersona: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (!datos.encuestas) {
    return (
      <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
        <Trans>Update the server to see the surveys.</Trans>
      </Text>
    );
  }
  return (
    <>
      <Bloque
        nombre="beta"
        titulo={t('Beta survey')}
        resumen={datos.encuestas.beta}
        onPersona={onPersona}
      />
      <Bloque
        nombre="dia40"
        titulo={t('Day 40 of the trial')}
        resumen={datos.encuestas.dia40}
        onPersona={onPersona}
      />
    </>
  );
}

function Bloque({
  nombre,
  titulo,
  resumen,
  onPersona,
}: {
  nombre: NombreEncuesta;
  titulo: string;
  resumen: ResumenEncuesta;
  onPersona: (id: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const preguntas = preguntasDe(t, nombre);
  const tituloDe = (clave: string) =>
    preguntas.find(
      p =>
        p.clave === clave ||
        (p.tipo === 'una-y-texto' && p.claveTexto === clave),
    )?.titulo ?? clave;

  return (
    <>
      <Seccion
        titulo={titulo}
        derecha={
          <Text style={{ ...texto.secundario, color: color.fg3 }}>
            {t('{{count}} answers', { count: resumen.respondidas })}
          </Text>
        }
      >
        {resumen.respondidas === 0 && (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            <Trans>Nobody has answered yet.</Trans>
          </Text>
        )}
      </Seccion>
      {resumen.respondidas > 0 &&
        preguntas
          .filter(p => p.tipo !== 'texto')
          .map(p => (
            <Seccion key={p.clave} titulo={p.titulo}>
              <ListaRecuentos
                filas={resumen.recuentos[p.clave] ?? []}
                nombre={c => nombreOpcion(p, c)}
              />
            </Seccion>
          ))}
      {resumen.textos.length > 0 && (
        <Seccion titulo={t('What people write')}>
          {resumen.textos.map(x => (
            <View
              key={`${x.userId}-${x.pregunta}`}
              style={{
                gap: 4,
                padding: '8px 0',
                borderTop: `1px solid ${color.line}`,
              }}
            >
              <Text style={{ ...texto.secundario, color: color.fg3 }}>
                {[
                  tituloDe(x.pregunta),
                  x.nombre,
                  fechaCorta(x.actualizado, locale),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
              <Text
                style={{
                  ...texto.cuerpo,
                  color: color.fg,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {x.texto}
              </Text>
            </View>
          ))}
        </Seccion>
      )}
      {resumen.contacto.length > 0 && (
        <Seccion titulo={t('Happy to chat for 10 minutes')}>
          {resumen.contacto.map(c => (
            <View
              key={c.userId}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <Text style={{ ...texto.cuerpo, color: color.fg }}>
                {c.nombre ?? t('No name')}
              </Text>
              <Boton variante="fantasma" onPress={() => onPersona(c.userId)}>
                <Trans>See person</Trans>
              </Boton>
            </View>
          ))}
        </Seccion>
      )}
    </>
  );
}

/** El texto de una opción tal como lo vio la persona. */
export function nombreOpcion(p: Pregunta, clave: string): string {
  if (p.tipo === 'una' || p.tipo === 'varias' || p.tipo === 'una-y-texto') {
    return p.opciones.find(o => o.valor === clave)?.texto ?? clave;
  }
  return clave;
}
